<template>
  <div v-if="content" class="level" :class="[content.type, content.status]">
    <VideoLevel
      v-if="content.type === 'video'"
      :courseId="course.id"
      :courseStatus="course.status"
      :exercises="content.exercises || undefined"
      :hasAccess="content.hasAccess"
      :nextVideo="nextVideo"
      :plansLink="plansLink"
      :quiz="content.quiz || undefined"
      :quizStatus="content.quiz ? content.quiz.status : ''"
      :title="content.file.name"
      :video="content.file"
      :videoStatus="content.status || defaultStatus"
    />
    <LabLevel
      v-if="content.type === 'lab'"
      :courseId="course.id"
      :courseStatus="course.status"
      :hasAccess="content.hasAccess"
      :lab="content.file"
      :labStatus="content.status || defaultStatus"
      :labType="content.labType"
      :plansLink="plansLink"
      :title="content.file.name"
    />
    <IFrameLevel
      v-if="content.type === 'iframe'"
      :courseId="course.id"
      :courseStatus="course.status"
      :iFrame="content.file"
      :iFrameStatus="content.status || defaultStatus"
      :plansLink="plansLink"
      :hasAccess="content.hasAccess"
      :title="content.file.name"
    />
  </div>
</template>

<script>
import { hasContentAccess } from '@/helpers'
import { mapState, mapGetters } from 'vuex'
import CONSTANTS from '@/constants'
import VideoLevel from './VideoLevel.vue'
import LabLevel from './LabLevel.vue'
import IFrameLevel from './IFrameLevel.vue'

export default {
  name: 'Level',
  components: {
    VideoLevel,
    LabLevel,
    IFrameLevel,
  },
  props: {
    level: {
      type: Object,
      required: true,
    },
    levelMedia: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      defaultStatus: CONSTANTS.CONTENT_USER_STATUS.UNSTARTED,
    }
  },
  computed: {
    ...mapState({
      course: state => state.courses.currentCourse,
      courseLabs: state => state.content.currentLabs,
      courseIFrames: state => state.content.currentIFrames,
      courseVideos: state => state.content.currentVideos,
      courseQuizzes: state => state.content.currentQuizzes,
      courseExercises: state => state.content.currentExercises,
      currentHoveredNote: state => state.content.currentHoveredNote,
      allLevels: state => state.courses.currentLevels,
      plansLink: state => state.app.plansLink,
    }),
    ...mapGetters({
      courseBookmarks: 'content/courseBookmarks',
    }),
    nextVideo() {
      const currentIndex = this.allLevels.indexOf(this.level)
      return this.allLevels[currentIndex + 1] || {}
    },
    content() {
      const {
        level: { content_type: type, uuid },
      } = this
      if (type === 'video') {
        return this.createVideo({ type, uuid })
      }
      if (type === 'iframe') {
        return this.createIframe({ type, uuid })
      }
      if (type === 'lab') {
        return this.createLab({ type, uuid })
      }
      return undefined
    },
  },
  methods: {
    createVideo({ type, uuid }) {
      const content = { type, uuid }
      const file = this.courseVideos.find(video => video.id === uuid)
      const { user_status: status } = file
      const { quiz: quizzes, exercise: exercises } = this.levelMedia[
        this.level.uuid
      ]
      if (quizzes && quizzes.length) {
        // ToDo: Fix this to support many quizzes in a level
        const quiz = this.courseQuizzes.find(q => q.id === quizzes[0].uuid)
        if (quiz) {
          content.quiz = {
            ...quiz,
            status: quiz.user_status,
          }
        }
      }
      if (exercises && exercises.length) {
        const exercisesIds = exercises.map(e => e.uuid)
        content.exercises = this.courseExercises.filter(e =>
          exercisesIds.includes(e.id)
        )
      }
      content.file = file
      content.status = status
      content.hasAccess = hasContentAccess({ parent: this.course, content })
      return content
    },
    createIframe({ type, uuid }) {
      const content = { type, uuid }
      const file = this.courseIFrames.find(iframe => iframe.id === uuid)
      const { user_status: status } = file
      content.file = file
      content.status = status
      content.hasAccess = hasContentAccess({ parent: this.course, content })
      return content
    },
    createLab({ type, uuid }) {
      const content = { type, uuid }
      const file = this.courseLabs.find(lab => lab.id === this.level.uuid)
      const { user_status: status, lab_type: labType } = file
      content.file = file
      content.status = status
      content.labType = labType
      content.hasAccess = hasContentAccess({ parent: this.course, content })
      return content
    },
  },
}
</script>
